import { Component, Input, OnInit } from '@angular/core';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import {
  DozentService,
  MoodleCourse,
} from '../../../services/dozent.service';
import { MoodleService } from '../../../services/moodle.service';

@Component({
  selector: 'app-moodle-courses-dialog',
  templateUrl: './moodle-courses-dialog.component.html',
  styleUrl: './moodle-courses-dialog.component.scss',
})
export class MoodleCoursesDialogComponent implements OnInit {
  @Input() onImportComplete: () => void = () => {};
  @Input() syncParticipants = true;
  /** When true, dialog acts as a single-pick selector — closes with the picked course instead of starting bulk import. */
  @Input() pickMode = false;
  @Input() onPick: (course: MoodleCourse) => void = () => {};
  /** When true, importing creates local course rows + syncs participants
   *  only (lightweight). When false (default), runs the full Wissensbasis
   *  bulk sync that processes sections, modules and embeddings. */
  @Input() metaOnly = false;

  courses: MoodleCourse[] = [];
  selectedCourses = new Set<string>();
  selectedCourseByKey = new Map<string, MoodleCourse>();
  courseTotal = 0;
  coursePage = 0;
  coursePageSize = 25;
  readonly pageSizeOptions = [25, 50, 100];
  loading = true;
  importing = false;

  constructor(
    protected dialogRef: NbDialogRef<MoodleCoursesDialogComponent>,
    private dozentService: DozentService,
    private moodleService: MoodleService,
    private toastrService: NbToastrService,
    private t: TranslateService
  ) {}

  ngOnInit() {
    this.loadCourses();
  }

  loadCourses() {
    this.loading = true;
    this.dozentService.listMoodleCoursesPage({
      limit: this.coursePageSize,
      offset: this.coursePage * this.coursePageSize,
    }).subscribe({
      next: (resp) => {
        this.courses = resp.data ?? [];
        this.courseTotal = resp.total ?? resp.pagination?.total ?? this.courses.length;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.toastrService.danger(
          this.moodleService.translateMoodleError(
            err,
            'pages.dozent.participants.noMoodleCourses'
          ),
          ''
        );
      },
    });
  }

  courseKey(course: MoodleCourse): string {
    return course.canonical_id || `${course.registration_id || 'default'}:${course.id}`;
  }

  isCourseDisabled(course: MoodleCourse): boolean {
    return course.registration_selectable === false;
  }

  getCourseDisabledHint(course: MoodleCourse): string {
    const site = course.site_name || course.site_url || this.t.instant('pages.dozent.participants.thisMoodleSite');
    return this.t.instant('pages.dozent.participants.moodleCourseDisabledHint', { site });
  }

  toggleCourse(course: MoodleCourse) {
    if (this.isCourseDisabled(course)) return;
    const key = this.courseKey(course);
    if (this.pickMode) {
      // Single-pick: clear and set this one
      this.selectedCourses.clear();
      this.selectedCourseByKey.clear();
      this.selectedCourses.add(key);
      this.selectedCourseByKey.set(key, course);
      return;
    }
    if (this.selectedCourses.has(key)) {
      this.selectedCourses.delete(key);
      this.selectedCourseByKey.delete(key);
    } else {
      this.selectedCourses.add(key);
      this.selectedCourseByKey.set(key, course);
    }
  }

  toggleAll() {
    if (this.allSelected) {
      this.selectableCourses.forEach((c) => {
        const key = this.courseKey(c);
        this.selectedCourses.delete(key);
        this.selectedCourseByKey.delete(key);
      });
    } else {
      this.selectableCourses.forEach((c) => {
        const key = this.courseKey(c);
        this.selectedCourses.add(key);
        this.selectedCourseByKey.set(key, c);
      });
    }
  }

  get selectableCourses(): MoodleCourse[] {
    return this.courses.filter((c) => !this.isCourseDisabled(c));
  }

  get allSelected(): boolean {
    return this.selectableCourses.length > 0 && this.selectableCourses.every((c) => this.selectedCourses.has(this.courseKey(c)));
  }

  onCoursePageChange(page: number): void {
    const maxPage = Math.max(Math.ceil(this.courseTotal / this.coursePageSize) - 1, 0);
    this.coursePage = Math.max(0, Math.min(page, maxPage));
    this.loadCourses();
  }

  onCoursePageSizeChange(size: number): void {
    this.coursePageSize = Math.min(Math.max(size, 1), 100);
    this.coursePage = 0;
    this.loadCourses();
  }

  startImport() {
    if (this.selectedCourses.size === 0) return;

    if (this.pickMode) {
      const picked = Array.from(this.selectedCourseByKey.values()).find((c) => !this.isCourseDisabled(c));
      if (picked) {
        this.onPick(picked);
      }
      this.dialogRef.close();
      return;
    }

    this.importing = true;
    const coursesToImport = Array.from(this.selectedCourseByKey.values())
      .filter((c) => !this.isCourseDisabled(c))
      .map((c) => ({
        moodle_course_id: c.id,
        course_name: c.fullname,
        registration_id: c.registration_id,
      }));

    if (this.metaOnly) {
      // Dozent flow: metadata + participants only, no content processing.
      this.dozentService.importMoodleCoursesMeta(coursesToImport).subscribe({
        next: () => {
          this.toastrService.success(
            this.t.instant('pages.dozent.participants.moodleImportStarted'),
            ''
          );
          this.importing = false;
          this.onImportComplete();
          this.dialogRef.close();
        },
        error: () => {
          this.toastrService.danger(this.t.instant('toasts.error'), '');
          this.importing = false;
        },
      });
      return;
    }

    this.moodleService.startBulkSync(coursesToImport).subscribe({
      next: () => {
        this.toastrService.success(
          this.t.instant('pages.dozent.participants.moodleImportStarted'),
          ''
        );
        this.importing = false;
        this.onImportComplete();
        this.dialogRef.close();
      },
      error: () => {
        this.toastrService.danger(this.t.instant('toasts.error'), '');
        this.importing = false;
      },
    });
  }

  close() {
    this.dialogRef.close();
  }
}
